/* eslint-disable react-hooks/exhaustive-deps */
import { downscaleImage } from "helpers/image-resize";
import React, { useEffect, useRef, useState } from "react";
import PageHeader from "components/page-header";
import MainLayout from "screens/partials/layout";
import { useAuth } from "context/auth-context";
import {
  getIdentifierFromSrc,
  validatePayload,
  loadSiteConfigRequest,
  saveSiteConfigRequest,
  uploadBusinessLogoRequest,
  uploadBusinessBannerRequest,
} from "queries/site-config-queries";

type Template = { id: string; name: string; src: string };

type TemplateProperties = {
  title: string;
  primaryColor: string;
  showHeader: boolean;
  branding: {
    name: string;
    logo?: string;
    favicon?: string;
    primaryColor: string;
    secondaryColor: string;
  };
  content: {
    heroTitle?: string;
    heroSubtitle?: string;
    aboutText?: string;
    features?: string[];
    heroImages?: string[];
  };
  info: {
    pageTitle: string;
    businessTel: string;
    businessName: string;
    description: string;
    businessBgStyle: string;
    businessButtonBgStyle: string;
    businessSocialsUrl: {
      Facebook?: string;
      Twitter?: string;
      Instagram?: string;
      LinkedIn?: string;
      Youtube?: string;
    };
    businessDomainName: string;
    businessId: string;
  };
};

// TODO: Update with real template URLs
// The first entry is the fallback selection, so keep it as the platform default (see
// DEFAULT_TEMPLATE in storbolt-api business/site_config_defaults.py).
const templates: Template[] = [
  {
    id: "templateA1",
    name: "Template A1 (Storbolt)",
    src: "https://stb-pv-a9f2.storbolt.com/?tenant=org3&preview=true",
  },
];

const PREVIEW_ORIGIN = "https://stb-pv-a9f2.storbolt.com";

const SettingsWebBuilder = () => {
  const { businessInfo } = useAuth();
  const [mode, setMode] = useState<"user" | "preview">("user"); // user mode = edit your business, preview = browse templates
  const [selectedSrc, setSelectedSrc] = useState<string>(templates[0].src);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    templates[0].id,
  );
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const [props, setProps] = useState<TemplateProperties>({
    title: "",
    primaryColor: "",
    showHeader: true,
    branding: {
      name: "",
      logo: "",
      favicon: "",
      primaryColor: "",
      secondaryColor: "",
    },
    content: {
      heroTitle: "",
      heroSubtitle: "",
      aboutText: "",
      features: [],
      heroImages: [],
    },
    info: {
      pageTitle: "",
      businessTel: "",
      businessName: "",
      description: "",
      businessBgStyle: "",
      businessButtonBgStyle: "",
      businessSocialsUrl: {},
      businessDomainName: "",
      businessId: "",
    },
  });
  // Helper to produce an empty props object (used to avoid prefill)
  const emptyProps = (): TemplateProperties => ({
    title: "",
    primaryColor: "",
    showHeader: true,
    branding: {
      name: "",
      logo: "",
      favicon: "",
      primaryColor: "",
      secondaryColor: "",
    },
    content: {
      heroTitle: "",
      heroSubtitle: "",
      aboutText: "",
      features: [],
      heroImages: [],
    },
    info: {
      pageTitle: "",
      businessTel: "",
      businessName: "",
      description: "",
      businessBgStyle: "",
      businessButtonBgStyle: "",
      businessSocialsUrl: {},
      businessDomainName: "",
      businessId: "",
    },
  });

  // Helper to prefill editor with existing business data
  const prefillFromBusinessData = (): TemplateProperties => {
    // Start with empty props
    const base = emptyProps();

    // If we have server-saved config, use that as the primary source
    if (previousConfig) {
      return {
        ...base,
        title:
          previousConfig.content?.heroTitle ||
          previousConfig.branding?.name ||
          previousConfig.info?.businessName ||
          businessInfo?.businessName ||
          "",
        branding: {
          name:
            previousConfig.branding?.name ||
            previousConfig.info?.businessName ||
            businessInfo?.businessName ||
            "",
          logo: previousConfig.branding?.logo || "",
          favicon: previousConfig.branding?.favicon || "",
          primaryColor: previousConfig.branding?.primaryColor || "",
          secondaryColor: previousConfig.branding?.secondaryColor || "",
        },
        content: {
          heroTitle: previousConfig.content?.heroTitle || "",
          heroSubtitle: previousConfig.content?.heroSubtitle || "",
          aboutText: previousConfig.content?.aboutText || "",
          features: previousConfig.content?.features || [],
          heroImages: previousConfig.content?.heroImages || [],
        },
        info: {
          pageTitle:
            previousConfig.info?.pageTitle ||
            previousConfig.info?.businessName ||
            businessInfo?.businessName ||
            "",
          businessTel: previousConfig.info?.businessTel || "",
          businessName:
            previousConfig.info?.businessName ||
            businessInfo?.businessName ||
            "",
          description: previousConfig.info?.description || "",
          businessBgStyle: previousConfig.info?.businessBgStyle || "",
          businessButtonBgStyle:
            previousConfig.info?.businessButtonBgStyle || "",
          businessSocialsUrl: previousConfig.info?.businessSocialsUrl || {},
          businessDomainName:
            previousConfig.info?.businessDomainName ||
            businessInfo?.businessDomain ||
            "",
          businessId:
            previousConfig.info?.businessId || businessInfo?.businessId || "",
        },
      };
    }

    // If no server config but we have businessInfo, use that
    if (businessInfo) {
      return {
        ...base,
        title: businessInfo.businessName || "",
        branding: {
          ...base.branding,
          name: businessInfo.businessName || "",
        },
        info: {
          ...base.info,
          pageTitle: businessInfo.businessName || "",
          businessName: businessInfo.businessName || "",
          businessDomainName: businessInfo.businessDomain || "",
          businessId: businessInfo.businessId || "",
        },
      };
    }

    // No data available, return empty
    return base;
  };
  const [saved, setSaved] = useState<boolean>(false);
  const [editingProperties, setEditingProperties] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(false);
  const [savingConfig, setSavingConfig] = useState<boolean>(false);
  const [serverErrors, setServerErrors] = useState<Record<string, any> | null>(
    null,
  );
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [previousConfig, setPreviousConfig] = useState<any | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingBanner, setUploadingBanner] = useState<boolean>(false);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const loadSiteConfig = async (identifier: string) => {
    setLoadingConfig(true);
    setServerMessage(null);
    setServerErrors(null);
    try {
      const data = await loadSiteConfigRequest(identifier);
      setPreviousConfig(data);

      // Set the template ID from loaded data
      if (data.template) {
        setSelectedTemplateId(data.template);
        const template = templates.find((t) => t.id === data.template);
        if (template) {
          setSelectedSrc(template.src);
        }
      }

      // If the user is actively editing, don't overwrite the editor fields
      if (!editingProperties) {
        setProps(
          (p) =>
            ({
              ...p,
              title:
                data.content?.heroTitle ||
                data.branding?.name ||
                data.info?.businessName ||
                "",
              branding: {
                name: data.branding?.name || data.info?.businessName || "",
                logo: data.branding?.logo || "",
                favicon: data.branding?.favicon || "",
                primaryColor: data.branding?.primaryColor || "",
                secondaryColor: data.branding?.secondaryColor || "",
              },
              content: {
                heroTitle: data.content?.heroTitle || "",
                heroSubtitle: data.content?.heroSubtitle || "",
                aboutText: data.content?.aboutText || "",
                features: data.content?.features || [],
                heroImages: data.content?.heroImages || [],
              },
              info: {
                pageTitle:
                  data.info?.pageTitle || data.info?.businessName || "",
                businessTel: data.info?.businessTel || "",
                businessName: data.info?.businessName || "",
                description: data.info?.description || "",
                businessBgStyle: data.info?.businessBgStyle || "",
                businessButtonBgStyle: data.info?.businessButtonBgStyle || "",
                businessSocialsUrl: data.info?.businessSocialsUrl || {},
                businessDomainName: data.info?.businessDomainName || "",
                businessId: data.info?.businessId || data.siteId || "",
              },
            }) as any,
        );
      }
      setServerMessage(null);
    } catch (err: any) {
      if (err.message?.includes("404")) {
        setServerMessage(
          "No site configuration found. You can create one by customizing a template and clicking Save.",
        );
      } else {
        setServerMessage(err.message || "Network error");
      }
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    // In user mode, load the user's business config automatically
    // In preview mode, load based on selectedSrc template identifier
    if (mode === "user" && businessInfo?.businessId) {
      loadSiteConfig(businessInfo.businessId);
    } else if (mode === "preview") {
      const id = getIdentifierFromSrc(selectedSrc);
      if (id) {
        loadSiteConfig(id);
      }
    }
  }, [mode, businessInfo?.businessId]);

  const saveSiteConfig = async () => {
    // Determine an identifier for saving. Prefer businessId in user mode,
    // otherwise try to extract from the selectedSrc. If still missing,
    // fall back to localStorage save for preview mode.
    let identifier = "";

    if (mode === "user" && businessInfo?.businessId) {
      identifier = businessInfo.businessId;
    }

    if (!identifier) {
      const idFromSrc = getIdentifierFromSrc(selectedSrc || "");
      if (idFromSrc) identifier = idFromSrc;
    }

    // Save exactly what the live preview is showing, so the published site matches it.
    const built = buildSiteConfig();
    const payload: any = {
      template: selectedTemplateId || undefined,
      theme: built.theme,
      branding: built.branding,
      content: built.content,
      info: {
        ...built.info,
        businessId: built.info.businessId === "preview" ? "" : built.info.businessId,
      },
    };
    const v = validatePayload(payload);
    if (Object.keys(v).length) {
      setServerErrors(v);
      return;
    }
    // If we couldn't determine a server-side identifier, save locally
    if (!identifier) {
      // Local save keyed by template id (if available)
      const key = `templateProps:${selectedTemplateId || "preview"}`;
      localStorage.setItem(key, JSON.stringify(props));
      setSaved(true);
      setServerMessage("Saved locally (preview mode)");
      return;
    }

    // Otherwise attempt server save
    setSavingConfig(true);
    setServerErrors(null);
    setServerMessage(null);
    try {
      setPreviousConfig((prev) => prev || null);
      const data = await saveSiteConfigRequest(identifier, payload);
      setPreviousConfig(data);
      setProps(
        (p) =>
          ({
            ...p,
            title: data.content?.heroTitle || p.title,
            branding: {
              ...p.branding,
              ...data.branding,
            },
            info: {
              ...p.info,
              ...data.info,
            },
          }) as any,
      );
      setServerMessage("Saved successfully");
    } catch (err: any) {
      if (err.validationErrors) {
        setServerErrors(err.validationErrors);
      } else {
        setServerMessage(err.message || "Network error");
      }
    } finally {
      setSavingConfig(false);
    }
  };

  const undoToPrevious = () => {
    if (!previousConfig) return;
    const data = previousConfig;
    setProps(
      (p) =>
        ({
          ...p,
          title: data.content?.heroTitle || p.title,
          branding: {
            ...p.branding,
            ...data.branding,
          },
          info: {
            ...p.info,
            ...data.info,
          },
        }) as any,
    );
    setServerMessage("Reverted to previous snapshot");
  };

  const MAX_BANNERS = 2;
  const MAX_ORIGINAL_BYTES = 20 * 1024 * 1024;

  const handleBannerUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = ""; // allow re-selecting the same file
    if (!file) return;

    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      setBannerError("Please upload a PNG, JPG, or WebP image");
      return;
    }
    if (file.size > MAX_ORIGINAL_BYTES) {
      setBannerError("File size exceeds 20MB limit");
      return;
    }
    const identifier = businessInfo?.businessId || "";
    if (!identifier) {
      setBannerError("Cannot determine business");
      return;
    }

    setUploadingBanner(true);
    setBannerError(null);
    try {
      const { url } = await uploadBusinessBannerRequest(
        identifier,
        await downscaleImage(file, 2400),
      );
      setProps((p) => ({
        ...p,
        content: {
          ...p.content,
          heroImages: [...(p.content?.heroImages || []), url].slice(0, MAX_BANNERS),
        },
      }));
      setEditingProperties(true);
    } catch (err: any) {
      setBannerError(err.message || "Upload failed");
    } finally {
      setUploadingBanner(false);
    }
  };

  const removeBanner = (index: number) => {
    setProps((p) => ({
      ...p,
      content: {
        ...p.content,
        heroImages: (p.content?.heroImages || []).filter((_, i) => i !== index),
      },
    }));
    setEditingProperties(true);
  };

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setUploadError("Please upload a PNG, JPG, or WebP image");
      return;
    }

    // Large originals are fine: they are shrunk in the browser before uploading.
    if (file.size > MAX_ORIGINAL_BYTES) {
      setUploadError("File size exceeds 20MB limit");
      return;
    }

    setUploadingLogo(true);
    setUploadError(null);

    try {
      const identifier = businessInfo?.businessId || "";
      if (!identifier) {
        setUploadError("Cannot determine business");
        return;
      }

      const data = await uploadBusinessLogoRequest(
        identifier,
        await downscaleImage(file, 1200),
      );

      // Update props with returned URLs
      setProps({
        ...props,
        branding: {
          ...props.branding,
          logo: data.logo_url || "",
          favicon: data.favicon_url || "",
        },
      });

      setServerMessage(
        data.background_removed === false
          ? "Logo and favicon uploaded (background could not be removed this time)"
          : "Logo and favicon uploaded successfully",
      );
    } catch (err: any) {
      setUploadError(err.message || "Upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };
  // Restore saved template props only when NOT actively editing
  useEffect(() => {
    if (!selectedTemplateId) return;
    if (editingProperties) return; // don't auto-fill while user is editing
    const raw = localStorage.getItem(`templateProps:${selectedTemplateId}`);
    if (raw) {
      try {
        setProps(JSON.parse(raw));
      } catch {
        // ignore parse errors
      }
    }
  }, [selectedTemplateId, editingProperties]);

  // Listen for initial config from Storefront to populate the editor
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "SITE_CONFIG_LOADED") {
        const cfg = event.data.config;
        console.log("Admin received initial config from storefront:", cfg);
        // Do not prefill editor from storefront messages while user is editing
        if (editingProperties) return;

        // Map SiteConfig to TemplateProperties for the form fields
        // But only if we don't have local saved changes already
        const hasSaved = localStorage.getItem(`templateProps:${cfg.template}`);
        if (!hasSaved) {
          const siteName =
            cfg.branding?.name ||
            cfg.info?.pageTitle ||
            cfg.info?.businessName ||
            "";
          setProps({
            title: siteName,
            primaryColor: cfg.branding?.primaryColor || "",
            showHeader: true,
            branding: {
              name: siteName,
              logo: cfg.branding?.logo || "",
              favicon: cfg.branding?.favicon || "",
              primaryColor: cfg.branding?.primaryColor || "",
              secondaryColor: cfg.branding?.secondaryColor || "",
            },
            content: {
              heroTitle: cfg.content?.heroTitle || "",
              heroSubtitle: cfg.content?.heroSubtitle || "",
              aboutText: cfg.content?.aboutText || "",
              features: cfg.content?.features || [],
              heroImages: cfg.content?.heroImages || [],
            },
            info: {
              pageTitle: siteName,
              businessTel: cfg.info?.businessTel || "",
              businessName: siteName,
              description:
                cfg.content?.aboutText || cfg.info?.description || "",
              businessBgStyle: cfg.info?.businessBgStyle || "",
              businessButtonBgStyle: cfg.info?.businessButtonBgStyle || "",
              businessSocialsUrl: cfg.info?.businessSocialsUrl || {},
              businessDomainName: cfg.info?.businessDomainName || "",
              businessId: cfg.siteId || "",
            },
          });
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(t);
  }, [saved]);

  // Editing your own site previews your real storefront (your products, your saved config); the
  // template gallery keeps using the shared demo tenants.
  const iframeSrc =
    mode === "user" && businessInfo?.businessDomain
      ? `${PREVIEW_ORIGIN}/?tenant=${encodeURIComponent(businessInfo.businessDomain)}&preview=true`
      : selectedSrc;

  // Build a SiteConfig-like object from current props/template selection
  const buildSiteConfig = () => {
    return {
      // The real business id, so the preview loads this business's own products and data.
      siteId: props.info?.businessId || businessInfo?.businessId || "preview",
      template: selectedTemplateId || templates[0].id,
      theme: "light",
      branding: {
        ...(props.branding || {}),
        name: props.title || props.branding?.name || "", // Map to Site Title
      },
      content: {
        heroTitle: props.content?.heroTitle || props.title,
        heroSubtitle: props.content?.heroSubtitle || "",
        aboutText: props.info.description || "",
        // No editor for these yet: pass through what the site already has instead of wiping it.
        features: (props.content?.features || []) as any,
        heroImages: props.content?.heroImages || [],
      },
      info: {
        ...(props.info || {}),
        pageTitle: props.title || props.info?.pageTitle || "", // Map to Site Title
        businessName: props.title || props.info?.businessName || "", // Map to Site Title
        businessId:
          props.info?.businessId || businessInfo?.businessId || "preview",
      },
    } as any;
  };

  // Post the preview config to the iframe (debounced)
  useEffect(() => {
    // PREVENT GLITCH: Don't push empty config if we aren't editing yet
    // Allow pushing even without branding.name to support new sites
    if (!editingProperties) return;

    const cfg = buildSiteConfig();
    const id = setTimeout(() => {
      const win = iframeRef.current?.contentWindow;
      if (win) {
        win.postMessage({ type: "SITE_CONFIG_PREVIEW", config: cfg }, "*");
      }
    }, 150);
    return () => clearTimeout(id);
  }, [props, iframeSrc, selectedTemplateId, viewMode, editingProperties]);

  const handleIframeLoad = () => {
    // Only push if we are currently in editing mode
    if (!editingProperties) return;

    const cfg = buildSiteConfig();
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.postMessage({ type: "SITE_CONFIG_PREVIEW", config: cfg }, "*");
    }
  };

  // Determine if there's no published template for the current context.
  const noSiteConfigured =
    (mode === "user" &&
      previousConfig &&
      (previousConfig.template === "" || previousConfig.template == null)) ||
    (mode === "preview" &&
      (selectedTemplateId === "" || selectedTemplateId === null));

  const noSiteForUser =
    mode === "user" &&
    previousConfig &&
    (previousConfig.template === "" || previousConfig.template == null);

  const canEditUserSite =
    mode === "user" && previousConfig && previousConfig.template;

  return (
    <MainLayout>
      <div>
        <PageHeader
          eyebrow="Settings"
          title="Web Builder"
          description="Customize your storefront appearance"
        />
        {/* Workspace: settings panel beside the live preview on wide screens, stacked on phones. */}
        <div className="flex flex-col overflow-hidden rounded-20px border bg-card lg:h-[calc(100dvh-15rem)] lg:min-h-[640px] lg:flex-row">
          <aside className="flex w-full flex-none flex-col overflow-hidden border-b border-default-200 lg:w-80 lg:border-b-0 lg:border-e">
            <div className="p-5 border-b border-default-200">
              {/* Mode Switcher */}
              {businessInfo && (
                <div className="bg-default-100 rounded-full p-1 flex gap-1">
                  <button
                    onClick={() => setMode("user")}
                    className={`flex-1 py-2 px-3 rounded-full text-xs font-medium transition-all ${
                      mode === "user"
                        ? "bg-card text-default-900 shadow-sm"
                        : "text-default-600 hover:text-default-900"
                    }`}
                  >
                    My Site
                  </button>
                  <button
                    onClick={() => setMode("preview")}
                    className={`flex-1 py-2 px-3 rounded-full text-xs font-medium transition-all ${
                      mode === "preview"
                        ? "bg-card text-default-900 shadow-sm"
                        : "text-default-600 hover:text-default-900"
                    }`}
                  >
                    Preview Templates
                  </button>
                </div>
              )}

              {mode === "user" && businessInfo && (
                <div className="mt-3 px-3 py-2.5 bg-default-50 rounded-[14px]">
                  <p className="text-xs text-default-700">
                    <span className="font-semibold">Editing:</span>{" "}
                    {businessInfo.businessName}
                  </p>
                  <p className="text-xs text-default-600 mt-0.5">
                    {businessInfo.businessDomain}
                  </p>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-5 scrollbar-thin scrollbar-thumb-slate-200">
              {loadingConfig && (
                <div className="mb-4 p-3 bg-info/10 border border-info/30 rounded-lg text-sm text-info">
                  Loading site configuration...
                </div>
              )}
              {serverMessage && !editingProperties && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm ${
                    serverMessage.includes("404") ||
                    serverMessage.includes("No site")
                      ? "bg-warning/10 border border-warning/30 text-warning"
                      : "bg-default-50 border border-default-200 text-default-700"
                  }`}
                >
                  {serverMessage}
                </div>
              )}
              {!editingProperties ? (
                <>
                  {mode === "user" && !businessInfo && (
                    <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg">
                      <p className="text-sm font-medium text-warning mb-2">
                        No Business Account
                      </p>
                      <p className="text-xs text-warning">
                        You need to have a business account to use the web
                        builder. Contact your administrator or switch to
                        "Preview Templates" mode to browse examples.
                      </p>
                    </div>
                  )}

                  {mode === "preview" && (
                    <div className="space-y-4">
                      <div className="text-xs font-semibold text-default uppercase tracking-wider mb-3">
                        Available Templates
                      </div>
                      {templates.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => {
                            setSelectedSrc(t.src);
                            setSelectedTemplateId(t.id);
                          }}
                          className={`group w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                            selectedSrc === t.src
                              ? "bg-primary-50 border-primary-500 shadow-sm"
                              : "bg-card border-default-200 hover:border-default-400 hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className={`font-semibold ${
                                selectedSrc === t.src
                                  ? "text-primary-900"
                                  : "text-default-700"
                              }`}
                            >
                              {t.name}
                            </span>
                            {selectedSrc === t.src && (
                              <span className="flex h-2 w-2 rounded-full bg-primary-500" />
                            )}
                          </div>
                          <div className="text-xs text-default truncate font-mono">
                            {t.id}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {mode === "user" && businessInfo && (
                    <div className="space-y-3">
                      <div className="text-xs font-semibold text-default uppercase tracking-wider">
                        Your Site
                      </div>
                      <button
                        onClick={() => {
                          if (canEditUserSite) {
                            // Prefill editor with existing business/server data
                            setProps(prefillFromBusinessData());
                            setEditingProperties(true);
                          } else {
                            // Guide the user to preview templates to create a site
                            setMode("preview");
                          }
                        }}
                        aria-disabled={!canEditUserSite}
                        className={`w-full p-4 rounded-xl border-2 transition-all group ${
                          canEditUserSite
                            ? "border-primary-200 bg-default-50 hover:bg-default-100"
                            : "border-default-100 bg-default-50 text-default cursor-not-allowed"
                        }`}
                        title={
                          canEditUserSite
                            ? "Customize your existing site"
                            : "No live site — browse templates to create one"
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-default-900 mb-1">
                              {props.title ||
                                businessInfo.businessName ||
                                "Untitled Site"}
                            </div>
                            <div className="text-xs text-default-600">
                              Click to customize
                            </div>
                            {noSiteForUser && (
                              <div className="text-xs mt-1 text-warning font-medium">
                                No live site configured
                              </div>
                            )}
                          </div>
                          <svg
                            className="w-5 h-5 text-default-600 group-hover:translate-x-1 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between pb-4 border-b border-default-100">
                    <h3 className="font-semibold text-default-800">Editor</h3>
                    <button
                      onClick={() => setEditingProperties(false)}
                      className="text-xs font-medium text-default-500 hover:text-default-800 px-2 py-1 rounded hover:bg-default-100 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>

                  {loadingConfig && (
                    <div className="p-3 bg-info/10 border border-info/30 rounded-lg text-sm text-info">
                      Loading site configuration...
                    </div>
                  )}
                  {serverMessage && (
                    <div
                      className={`p-3 rounded-lg text-sm ${
                        serverMessage.includes("successfully")
                          ? "bg-success/10 border border-success/30 text-success"
                          : serverMessage.includes("404") ||
                              serverMessage.includes("No site")
                            ? "bg-warning/10 border border-warning/30 text-warning"
                            : serverMessage.includes("error") ||
                                serverMessage.includes("Validation")
                              ? "bg-destructive/10 border border-destructive/30 text-destructive"
                              : "bg-default-50 border border-default-200 text-default-700"
                      }`}
                    >
                      {serverMessage}
                    </div>
                  )}

                  {!selectedTemplateId ? (
                    <div className="text-sm text-default-500 italic text-center py-8">
                      Select a template to begin editing.
                    </div>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!selectedTemplateId) return;
                        saveSiteConfig();
                      }}
                      className="space-y-6"
                    >
                      {/* General Section */}
                      <div className="space-y-4">
                        <div className="text-xs font-bold text-default uppercase tracking-wider">
                          General
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-default mb-1.5">
                            Site Title
                          </label>
                          <input
                            value={props.title}
                            onChange={(e) =>
                              setProps({ ...props, title: e.target.value })
                            }
                            className="block w-full rounded-full border-transparent bg-default-100 px-4 py-2.5 text-sm focus:border-default-900 focus:bg-card focus:ring-2 focus:ring-default-900/10 transition-all outline-none"
                            placeholder="My Awesome Store"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-default mb-1.5">
                            Hero Title
                          </label>
                          <input
                            value={props.content?.heroTitle || ""}
                            onChange={(e) =>
                              setProps({
                                ...props,
                                content: {
                                  ...(props.content || {}),
                                  heroTitle: e.target.value,
                                },
                              })
                            }
                            className="block w-full rounded-full border-transparent bg-default-100 px-4 py-2.5 text-sm focus:border-default-900 focus:bg-card focus:ring-2 focus:ring-default-900/10 transition-all outline-none"
                            placeholder="Big headline for the homepage"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-default mb-1.5">
                            Hero Subtitle
                          </label>
                          <input
                            value={props.content?.heroSubtitle || ""}
                            onChange={(e) =>
                              setProps({
                                ...props,
                                content: {
                                  ...(props.content || {}),
                                  heroSubtitle: e.target.value,
                                },
                              })
                            }
                            className="block w-full rounded-full border-transparent bg-default-100 px-4 py-2.5 text-sm focus:border-default-900 focus:bg-card focus:ring-2 focus:ring-default-900/10 transition-all outline-none"
                            placeholder="Short supporting text"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-default mb-1.5">
                            Domain Name
                          </label>
                          <div className="flex rounded-md shadow-sm">
                            <input
                              type="text"
                              name="company-website"
                              id="company-website"
                              readOnly
                              aria-readonly="true"
                              className="block w-full min-w-0 flex-1 rounded-none rounded-l-lg border-default-200 bg-default-50 p-2.5 text-sm transition-all outline-none"
                              placeholder="subdomain"
                              value={props.info.businessDomainName.replace(
                                ".storbolt.com",
                                "",
                              )}
                            />
                            <span className="inline-flex items-center rounded-r-md border border-l-0 border-default-200 bg-default-100 px-3 text-default-500 sm:text-sm">
                              .storbolt.com
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Branding Section */}
                      <div className="space-y-4">
                        <div className="text-xs font-bold text-default uppercase tracking-wider">
                          Branding
                        </div>

                        {/* Logo Upload */}
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-default-700">
                            Business Logo
                          </label>
                          <p className="text-xs text-default-500 mb-2">
                            Upload your business logo (PNG, JPG or WebP). We'll
                            automatically create a favicon for you.
                          </p>

                          {uploadError && (
                            <div className="p-2 bg-destructive/10 border border-destructive/30 rounded-lg text-xs text-destructive">
                              {uploadError}
                            </div>
                          )}

                          <div className="flex items-center gap-4">
                            {props.branding.logo && (
                              <div className="flex-shrink-0">
                                <img
                                  src={props.branding.logo}
                                  alt="Current logo"
                                  className="h-16 w-16 object-contain rounded-lg border border-default-200 bg-white p-2"
                                />
                              </div>
                            )}

                            <div className="flex-1">
                              <label
                                htmlFor="logo-upload"
                                className={
                                  "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer " +
                                  (uploadingLogo
                                    ? "bg-default-400 text-default-foreground cursor-not-allowed"
                                    : "bg-default-900 text-default-foreground hover:bg-default-800 hover:scale-105 active:scale-95")
                                }
                              >
                                {uploadingLogo ? (
                                  <>
                                    <svg
                                      className="animate-spin h-4 w-4"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                      />
                                    </svg>
                                    {props.branding.logo
                                      ? "Change Logo"
                                      : "Upload Logo"}
                                  </>
                                )}
                              </label>
                              <input
                                id="logo-upload"
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                onChange={handleLogoUpload}
                                disabled={uploadingLogo}
                                className="hidden"
                              />
                            </div>
                          </div>

                          {props.branding.favicon && (
                            <div className="flex items-center gap-2 pt-2">
                              <img
                                src={props.branding.favicon}
                                alt="Favicon"
                                className="h-8 w-8 object-contain rounded border border-default-200 bg-white"
                              />
                              <span className="text-xs text-default-600">
                                Generated favicon
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Banner Images */}
                        <div className="space-y-2 pt-2">
                          <label className="block text-sm font-medium text-default-700">
                            Banner Images
                          </label>
                          <p className="text-xs text-default-500 mb-2">
                            Add one image for a hero banner, or two to show a
                            rotating carousel (PNG, JPG or WebP). Click Save to
                            publish.
                          </p>

                          {bannerError && (
                            <div className="p-2 bg-destructive/10 border border-destructive/30 rounded-lg text-xs text-destructive">
                              {bannerError}
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-3">
                            {(props.content?.heroImages || []).map(
                              (url, index) => (
                                <div
                                  key={url}
                                  className="relative rounded-lg overflow-hidden border border-default-200"
                                >
                                  <img
                                    src={url}
                                    alt={`Banner ${index + 1}`}
                                    className="h-20 w-full object-cover"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeBanner(index)}
                                    className="absolute top-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-black/80"
                                    aria-label={`Remove banner ${index + 1}`}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ),
                            )}
                          </div>

                          {(props.content?.heroImages || []).length <
                            MAX_BANNERS && (
                            <div>
                              <label
                                htmlFor="banner-upload"
                                className={
                                  "inline-flex cursor-pointer items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all " +
                                  (uploadingBanner
                                    ? "bg-default-200 text-default-500 cursor-not-allowed"
                                    : "bg-primary-50 text-primary-700 hover:bg-primary-100")
                                }
                              >
                                {uploadingBanner
                                  ? "Uploading..."
                                  : (props.content?.heroImages || []).length ===
                                      0
                                    ? "Add banner image"
                                    : "Add second banner (carousel)"}
                              </label>
                              <input
                                id="banner-upload"
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                onChange={handleBannerUpload}
                                disabled={uploadingBanner}
                                className="hidden"
                              />
                            </div>
                          )}
                        </div>

                        <p className="text-xs text-default-500 pt-2">
                          Text and icons on these colors switch between light
                          and dark automatically so they stay readable.
                        </p>
                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div>
                            <label className="block text-xs font-medium text-default-600 mb-2">
                              Header &amp; Footer Color
                            </label>
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full border border-default-200 shadow-sm overflow-hidden flex-shrink-0 relative">
                                <input
                                  type="color"
                                  value={props.branding.primaryColor}
                                  onChange={(e) =>
                                    setProps({
                                      ...props,
                                      branding: {
                                        ...props.branding,
                                        primaryColor: e.target.value,
                                      },
                                      info: {
                                        ...props.info,
                                        businessBgStyle: e.target.value,
                                      },
                                    })
                                  }
                                  className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer opacity-0"
                                />
                                <div
                                  className="w-full h-full"
                                  style={{
                                    backgroundColor:
                                      props.branding.primaryColor,
                                  }}
                                />
                              </div>
                              <span className="text-xs font-mono text-default-500 uppercase">
                                {props.branding.primaryColor}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-default-600 mb-2">
                              Button Color
                            </label>
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full border border-default-200 shadow-sm overflow-hidden flex-shrink-0 relative">
                                <input
                                  type="color"
                                  value={props.branding.secondaryColor}
                                  onChange={(e) =>
                                    setProps({
                                      ...props,
                                      branding: {
                                        ...props.branding,
                                        secondaryColor: e.target.value,
                                      },
                                      info: {
                                        ...props.info,
                                        businessButtonBgStyle: e.target.value,
                                      },
                                    })
                                  }
                                  className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer opacity-0"
                                />
                                <div
                                  className="w-full h-full"
                                  style={{
                                    backgroundColor:
                                      props.branding.secondaryColor,
                                  }}
                                />
                              </div>
                              <span className="text-xs font-mono text-default-500 uppercase">
                                {props.branding.secondaryColor}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Contact & Info */}
                      <div className="space-y-4">
                        <div className="text-xs font-bold text-default uppercase tracking-wider">
                          Contact Info
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-default-700 mb-1.5">
                            Phone Number
                          </label>
                          <input
                            value={props.info.businessTel}
                            onChange={(e) =>
                              setProps({
                                ...props,
                                info: {
                                  ...props.info,
                                  businessTel: e.target.value,
                                },
                              })
                            }
                            className="block w-full rounded-full border-transparent bg-default-100 px-4 py-2.5 text-sm focus:border-default-900 focus:bg-card focus:ring-2 focus:ring-default-900/10 transition-all outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-default-700 mb-1.5">
                            About / Description
                          </label>
                          <textarea
                            rows={3}
                            value={props.info.description}
                            onChange={(e) =>
                              setProps({
                                ...props,
                                info: {
                                  ...props.info,
                                  description: e.target.value,
                                },
                              })
                            }
                            className="block w-full rounded-full border-transparent bg-default-100 px-4 py-2.5 text-sm focus:border-default-900 focus:bg-card focus:ring-2 focus:ring-default-900/10 transition-all outline-none resize-none"
                          />
                        </div>
                      </div>

                      {/* Socials */}
                      <div className="space-y-4">
                        <div className="text-xs font-bold text-default uppercase tracking-wider">
                          Social Media
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          {[
                            "Facebook",
                            "Twitter",
                            "Instagram",
                            "LinkedIn",
                            "Youtube",
                          ].map((social) => (
                            <div className="relative" key={social}>
                              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-default text-xs font-medium">
                                {social}
                              </div>
                              <input
                                placeholder="https://..."
                                value={
                                  props.info.businessSocialsUrl[
                                    social as keyof typeof props.info.businessSocialsUrl
                                  ] || ""
                                }
                                onChange={(e) =>
                                  setProps({
                                    ...props,
                                    info: {
                                      ...props.info,
                                      businessSocialsUrl: {
                                        ...props.info.businessSocialsUrl,
                                        [social]: e.target.value,
                                      },
                                    },
                                  })
                                }
                                className="block w-full rounded-full border-transparent bg-default-100 py-2.5 pl-24 pr-3 text-sm focus:border-default-900 focus:bg-card focus:ring-2 focus:ring-default-900/10 transition-all outline-none"
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="pt-4 border-t border-default-100 flex flex-col gap-2 sticky bottom-0 bg-card pb-2">
                        {serverErrors &&
                          Object.keys(serverErrors).length > 0 && (
                            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                              <div className="text-sm font-medium text-destructive mb-2">
                                Validation Errors:
                              </div>
                              <ul className="text-xs text-destructive space-y-1">
                                {Object.entries(serverErrors).map(
                                  ([key, value]) => (
                                    <li key={key}>
                                      <span className="font-semibold">
                                        {key}:
                                      </span>{" "}
                                      {String(value)}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                      </div>
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedTemplateId) return;
                            localStorage.removeItem(
                              `templateProps:${selectedTemplateId}`,
                            );
                            setProps({
                              title: "",
                              primaryColor: "",
                              showHeader: true,
                              branding: {
                                name: "",
                                logo: "",
                                favicon: "",
                                primaryColor: "",
                                secondaryColor: "",
                              },
                              content: {
                                heroTitle: "",
                                heroSubtitle: "",
                                aboutText: "",
                                features: [],
                                heroImages: [],
                              },
                              info: {
                                pageTitle: "",
                                businessTel: "",
                                businessName: "",
                                description: "",
                                businessBgStyle: "",
                                businessButtonBgStyle: "",
                                businessSocialsUrl: {},
                                businessDomainName: "",
                                businessId: "",
                              },
                            });
                            setSaved(true);
                          }}
                          className="px-4 py-2 rounded-lg text-xs font-medium text-default-500 hover:text-default-700 hover:bg-default-50 transition-colors"
                        >
                          Reset Defaults
                        </button>
                        <div className="flex items-center gap-3">
                          {saved && (
                            <span className="text-xs font-medium text-success animate-pulse">
                              Saved!
                            </span>
                          )}
                          {previousConfig && (
                            <button
                              type="button"
                              onClick={undoToPrevious}
                              className="px-3 py-2 rounded-lg border text-sm"
                            >
                              Undo
                            </button>
                          )}
                          <button
                            type="submit"
                            disabled={savingConfig}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all active:scale-95 ${
                              savingConfig
                                ? "bg-default-300 text-default-700 cursor-not-allowed"
                                : "bg-default-600 text-background hover:bg-default-700"
                            }`}
                          >
                            {savingConfig ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          </aside>

          <main className="flex-1 min-w-0 bg-default-50 relative overflow-hidden flex flex-col min-h-[560px]">
            <header className="bg-card border-b border-default-200 px-5 py-3.5 flex flex-wrap items-center justify-between gap-3 z-10">
              <div className="flex items-center gap-4">
                <h2 className="font-display text-lg font-semibold text-default-900">
                  Live Preview
                </h2>
                {selectedTemplateId && (
                  <span className="px-3 py-1 rounded-full bg-default-50 text-default-600 text-xs font-medium border border-default-200">
                    {templates.find((t) => t.id === selectedTemplateId)?.name}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                {/* View Mode Switcher */}
                <div className="bg-default-100 p-1 rounded-full flex items-center">
                  <button
                    onClick={() => setViewMode("desktop")}
                    className={`py-1.5 px-3 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
                      viewMode === "desktop"
                        ? "bg-card text-default-900 shadow-sm"
                        : "text-default-500 hover:text-default-700 hover:bg-default-200/50"
                    }`}
                    title="Desktop view"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    <span className="hidden sm:inline">Desktop</span>
                  </button>

                  <button
                    onClick={() => setViewMode("mobile")}
                    className={`py-1.5 px-3 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
                      viewMode === "mobile"
                        ? "bg-card text-default-900 shadow-sm"
                        : "text-default-500 hover:text-default-700 hover:bg-default-200/50"
                    }`}
                    title="Mobile view"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="7" y="2" width="10" height="20" rx="2" ry="2" />
                      <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                    <span className="hidden sm:inline">Mobile</span>
                  </button>
                </div>

                {/* Edit Button */}
                <button
                  onClick={() => {
                    const current = templates.find(
                      (t) => t.src === selectedSrc,
                    );
                    if (current) {
                      setSelectedTemplateId(current.id);
                      // Prefill editor with existing business/server data
                      setProps(prefillFromBusinessData());
                      setEditingProperties(true);
                    }
                  }}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-default text-default-foreground font-display text-sm font-semibold hover:bg-default/90 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Customize
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-auto flex items-center justify-center p-4 md:p-8 bg-default-100">
              {noSiteConfigured ? (
                <div className="w-full max-w-2xl p-8 bg-card rounded-20px border border-default-200 text-center">
                  <h3 className="text-lg font-semibold text-default-800 mb-2">
                    No live site configured
                  </h3>
                  <p className="text-sm text-default-600 mb-4">
                    It looks like you don't have a published template for your
                    site yet. Customize a template to create your storefront.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => setMode("preview")}
                      className="h-10 px-5 rounded-full border border-default-300 font-display text-sm font-semibold hover:bg-default-100 transition-colors"
                    >
                      Browse Templates
                    </button>
                  </div>
                </div>
              ) : viewMode === "desktop" ? (
                <div className="w-full h-full min-h-[480px] bg-white rounded-[14px] shadow-lg overflow-hidden border border-default-200">
                  <iframe
                    ref={iframeRef}
                    id="preview-iframe"
                    src={iframeSrc}
                    title="Template preview"
                    className="w-full h-full"
                    sandbox="allow-scripts allow-forms allow-same-origin"
                    allowFullScreen
                    onLoad={handleIframeLoad}
                  />
                </div>
              ) : (
                <div className="w-[375px] max-w-full h-[812px] mx-auto bg-white rounded-[28px] shadow-lg overflow-hidden border-[6px] border-default-900">
                  <iframe
                    ref={iframeRef}
                    id="preview-iframe"
                    src={iframeSrc}
                    title="Template preview mobile"
                    className="w-full h-full"
                    sandbox="allow-scripts allow-forms allow-same-origin"
                    allowFullScreen
                    onLoad={handleIframeLoad}
                  />
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </MainLayout>
  );
};

export default SettingsWebBuilder;
